import React, { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Grid,
  Typography,
  Snackbar,
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { useForm } from 'react-hook-form';
import { useManualQuery } from 'graphql-hooks';
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useAtomValue } from 'jotai';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { USER_EMAIL_EXIST, USER_USERNAME_EXIST } from '../../graphql/user/query';
import { CustomInput } from '../CustomInput';
import { DatePick } from '../DatePick';
import { useAuth, Context } from '../../context/auth';
import { isMobileAtom } from '../../atoms';
import styles from './style';

interface SignUpData {
  accessToken?: string;
  provider?: string;
  email: string;
  dateOfBirth: Date;
  password: string;
  username: string;
}

// Data of a social account without SoSkills account (Google, Apple, Facebook).
export interface SignUpPrefill {
  accessToken?: string;
  email?: string;
  provider?: string;
  username?: string;
}

interface SignUpFormProps {
  // In the login popup: the popup shows its own title.
  embedded?: boolean;
  // Replaces the navigation to /login (ex: the popup switches to its login form).
  onSwitchToLogin?: () => void;
  // Social sign up data. Default: the router state on the page, nothing in the popup
  // (the state of the page behind the popup is not about the sign up).
  prefill?: SignUpPrefill;
}

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Users must be at least 13 years old.
const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 13);
  return maxDate;
};

// Sign up form: account fields, the legal line and the link to the login. Fills its container.
const SignUpForm: React.FC<SignUpFormProps> = React.memo((props) => {
  const {
    embedded = false,
    onSwitchToLogin,
    prefill,
  } = props;
  const { t } = useTranslation();
  const {
    user,
    signUp,
  } = useAuth() as Context;
  const isMobile = useAtomValue(isMobileAtom);
  const location = useLocation() as any;

  const { control, handleSubmit } = useForm<SignUpData>();
  const [errorMessage, setErrorMessage] = useState({ active: false, msg: '' });
  const [userEmailExist] = useManualQuery(USER_EMAIL_EXIST);
  const [usernameAlreadyExist, setUsernameAlreadyExist] = useState(false);
  const [emailAlreadyExist, setEmailAlreadyExist] = useState(false);
  const [userUsernameExist] = useManualQuery(USER_USERNAME_EXIST);
  const maxDate = useMemo(() => getMaxDate(), []);

  const usernameTrad = t('username');
  const emailTrad = t('email');
  const formPasswordTrad = t('form.password');
  const formResumeTitleTrad = t('form.resumeTitle');
  const addYourDateOfBirthTrad = t('addYourDateOfBirth');
  const legalResumeTrad = t('form.legal.resume');
  const legalTermsTrad = t('form.legal.terms');
  const legalAndTrad = t('form.legal.and');
  const legalPrivacyTrad = t('form.legal.privacy');
  const formSignUpTrad = t('form.signUp');
  const formSignInQuestionTrad = t('form.signInQuestion');
  const formSignInTrad = t('form.signIn');
  const formErrorRequiredUsernameTrad = t('form.error.required.username');
  const formErrorMinLengthUsernameTrad = t('form.error.minLength.username');
  const formErrorMaxLengthUsernameTrad = t('form.error.maxLength.username');
  const formErrorRequiredEmailTrad = t('form.error.required.email');
  const formErrorPatternEmailTrad = t('form.error.pattern.email');
  const formErrorRequiredPasswordTrad = t('form.error.required.password');
  const formErrorMinLengthPasswordTrad = t('form.error.minLength.password');
  const formErrorPatternPasswordTrad = t('form.error.pattern.password');

  // The popup has its own title right above.
  const firstFieldMt = embedded ? 0 : 2;

  const socialState = useMemo(() => {
    if (prefill) {
      return prefill;
    }
    if (embedded) {
      return undefined;
    }
    return location ? location.state : undefined;
  }, [embedded, location, prefill]);

  // Memoized: a debounce created at each render would never wait.
  const debounceUsernameFn = useMemo(() => debounce(async (username: string) => {
    const { data } = await userUsernameExist({
      variables: {
        username,
      },
    }) as any;
    if (data) {
      setUsernameAlreadyExist(!!data.userUsernameExist);
    }
  }, 300), [userUsernameExist]);

  const debounceEmailFn = useMemo(() => debounce(async (email: string) => {
    const { data } = await userEmailExist({
      variables: {
        email,
      },
    }) as any;
    if (data) {
      setEmailAlreadyExist(!!data.userEmailExist);
    }
  }, 300), [userEmailExist]);

  const onChangeUsername = useCallback((e: any) => {
    debounceUsernameFn(e.target.value);
  }, [debounceUsernameFn]);

  const onChangeEmail = useCallback((e: any) => {
    debounceEmailFn(e.target.value);
  }, [debounceEmailFn]);

  // The birth date is always asked (a social account doesn't give it), never guessed.
  const onRegisterPressed = useCallback(async (data: SignUpData) => {
    if (!data.dateOfBirth) {
      setErrorMessage({ active: true, msg: addYourDateOfBirthTrad });
      return;
    }
    try {
      if (!usernameAlreadyExist && !emailAlreadyExist) {
        if (!isEmpty(socialState) && socialState.provider) {
          if (socialState.provider === 'Facebook' && socialState.accessToken) {
            data.accessToken = socialState.accessToken;
          }
          data.provider = socialState.provider;
        }
        await signUp(data);
      }
    } catch (e: any) {
      setErrorMessage({ active: true, msg: e.message });
    }
  }, [addYourDateOfBirthTrad, emailAlreadyExist, signUp, socialState, usernameAlreadyExist]);

  const onCloseSnack = useCallback(() => {
    setErrorMessage({ active: false, msg: '' });
  }, []);

  const onClickLogin = useCallback((e: React.MouseEvent) => {
    if (onSwitchToLogin) {
      e.preventDefault();
      onSwitchToLogin();
    }
  }, [onSwitchToLogin]);

  const usernameRules = useMemo(() => ({
    required: formErrorRequiredUsernameTrad,
    minLength: {
      value: 3,
      message: formErrorMinLengthUsernameTrad,
    },
    maxLength: {
      value: 24,
      message: formErrorMaxLengthUsernameTrad,
    },
  }), [
    formErrorRequiredUsernameTrad,
    formErrorMinLengthUsernameTrad,
    formErrorMaxLengthUsernameTrad,
  ]);

  const emailRules = useMemo(() => ({
    required: formErrorRequiredEmailTrad,
    pattern: {
      value: EMAIL_REGEX,
      message: formErrorPatternEmailTrad,
    },
  }), [formErrorRequiredEmailTrad, formErrorPatternEmailTrad]);

  const passwordRules = useMemo(() => ({
    required: formErrorRequiredPasswordTrad,
    minLength: {
      value: 8,
      message: formErrorMinLengthPasswordTrad,
    },
    pattern: {
      value: PASSWORD_REGEX,
      message: formErrorPatternPasswordTrad,
    },
  }), [
    formErrorRequiredPasswordTrad,
    formErrorMinLengthPasswordTrad,
    formErrorPatternPasswordTrad,
  ]);

  const linkSignIn = useMemo(() => ({
    ...styles.smallLink,
    marginLeft: isMobile ? '1vw' : '0.5vw',
  }), [isMobile]);

  const typoAlign = useMemo(() => isMobile ? 'center' : 'inherit', [isMobile]);

  return (
    <>
      <Grid
        container
        direction="row"
      >
        <Grid item mt={firstFieldMt} xs={12}>
          <CustomInput
            dark
            alreadyExist={usernameAlreadyExist}
            name="username"
            autoComplete="off"
            label={usernameTrad}
            control={control}
            onChange={onChangeUsername}
            placeholder={usernameTrad}
            value={socialState ? socialState.username : null}
            rules={usernameRules}
            fullWidth
            required
          />
        </Grid>
        <Grid item mt={2} xs={12}>
          <CustomInput
            dark
            alreadyExist={emailAlreadyExist}
            name="email"
            // Not "email": the saved login of the browser would fill an existing account.
            autoComplete="off"
            label='Email'
            control={control}
            onChange={onChangeEmail}
            placeholder={emailTrad}
            value={socialState ? socialState.email : null}
            rules={emailRules}
            fullWidth
            variant="outlined"
            required
          />
        </Grid>
        {!socialState && (
          <Grid item mt={2} xs={12}>
            <CustomInput
              dark
              name="password"
              autoComplete="new-password"
              control={control}
              label={formPasswordTrad}
              placeholder={formPasswordTrad}
              rules={passwordRules}
              fullWidth
              variant="outlined"
              type="password"
              required
            />
            <Typography sx={styles.hint}>
              {formResumeTitleTrad}
            </Typography>
          </Grid>
        )}
        <Grid item mt={3} xs={12}>
          <DatePick
            dark
            maxDate={maxDate}
            name="dateOfBirth"
            control={control}
            label={addYourDateOfBirthTrad}
            language={user.language}
            value={null}
          />
        </Grid>
        <Grid item mt={2} xs={12}>
          <Button
            fullWidth
            onClick={handleSubmit(onRegisterPressed)}
            sx={styles.signupButton}
          >
            <Typography
              fontWeight={500}
              fontFamily="Poppins"
            >
              {formSignUpTrad}
            </Typography>
          </Button>
        </Grid>
        {/* Like the app: signing up means accepting the terms, no box to tick. */}
        <Grid item xs={12} mt={1.5}>
          <Typography sx={styles.cguText}>
            {legalResumeTrad}
            {' '}
            <Link to="/terms" target="_blank" style={styles.linkTextDecoration}>{legalTermsTrad}</Link>
            {` ${legalAndTrad} `}
            <Link to="/privacy" target="_blank" style={styles.linkTextDecoration}>{legalPrivacyTrad}</Link>
            .
          </Typography>
        </Grid>
        <Grid
          item
          mt={1.5}
          mb={0}
          xs={12}
          flexDirection="row"
          display="flex"
          justifyContent="flex-end"
        >
          <Typography display="inline" sx={styles.smallText}>
            {formSignInQuestionTrad}
          </Typography>
          <Link to="/login" style={styles.linkTextDecoration} onClick={onClickLogin}>
            <Typography
              align={typoAlign}
              display="inline"
              sx={linkSignIn}
            >
              {formSignInTrad}
            </Typography>
          </Link>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={styles.anchorOrigin}
        open={errorMessage.active}
        autoHideDuration={3000}
        message={errorMessage.msg}
        onClose={onCloseSnack}
      >
        <Alert severity="error" sx={styles.alert}>
          {errorMessage.msg}
        </Alert>
      </Snackbar>
    </>
  );
});

export default SignUpForm;
